import { Link } from "react-router-dom";
import { MapPin, Users, Flame, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import TrustBadge from "./TrustBadge";
import { formatGBP } from "../lib/api";

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1556484687-30636164638b?crop=entropy&cs=srgb&fm=jpg&w=900&q=70";

const URGENCY = {
  critical: { label: "Critical", cls: "bg-red-50 text-red-600 border-red-200" },
  high: { label: "Urgent", cls: "bg-orange-50 text-orange-600 border-orange-200" },
  normal: { label: "Active", cls: "bg-brand-pink text-brand-text border-pink-200" },
  low: { label: "Ongoing", cls: "bg-stone-100 text-stone-500 border-stone-200" },
};

export default function CampaignCard({ campaign, index = 0 }) {
  const pct = Math.min(100, Math.round(((campaign.amount_raised || 0) / Math.max(1, campaign.amount_needed)) * 100));
  const urg = URGENCY[campaign.urgency] || URGENCY.normal;

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: Math.min(index * 0.04, 0.4), ease: "easeOut" }}
      data-testid={`campaign-card-${campaign.id}`}
    >
      <Link to={`/campaigns/${campaign.id}`} className="premium-card group block overflow-hidden h-full flex flex-col">
        <div className="relative h-56 overflow-hidden">
          <img
            src={campaign.cover_image_url || FALLBACK_IMG}
            alt={campaign.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onError={(e) => { e.target.src = FALLBACK_IMG; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          <span className={`absolute top-3 left-3 inline-flex items-center gap-1 text-[0.65rem] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full border ${urg.cls}`}>
            {campaign.urgency === "critical" || campaign.urgency === "high" ? <Flame size={11} strokeWidth={2} /> : null}
            {urg.label}
          </span>
          <div className="absolute top-3 right-3">
            <TrustBadge level={campaign.creator_verification} />
          </div>
        </div>

        <div className="p-6 flex-1 flex flex-col">
          <span className="eyebrow">{campaign.category}</span>
          <h3 className="font-serif-display text-xl mt-2 text-brand-wine leading-snug line-clamp-2 group-hover:text-brand-primary transition-colors">
            {campaign.title}
          </h3>
          <p className="text-brand-muted text-sm mt-2 line-clamp-2 leading-relaxed">{campaign.story}</p>

          <div className="flex items-center gap-3 mt-4 text-xs text-brand-muted">
            <span className="inline-flex items-center gap-1"><MapPin size={12} strokeWidth={1.6} />{campaign.location}</span>
            <span className="inline-flex items-center gap-1"><Users size={12} strokeWidth={1.6} />{campaign.donor_count || 0} donors</span>
          </div>

          <div className="mt-5 mb-3">
            <div className="w-full h-1.5 bg-brand-pink rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{ width: `${pct}%`, background: "linear-gradient(90deg, #E91E63 0%, #FF4F8A 50%, #FFB72E 100%)" }}
                data-testid={`campaign-progress-${campaign.id}`}
              />
            </div>
          </div>

          <div className="flex items-end justify-between mt-auto">
            <div>
              <p className="font-serif-display text-2xl text-brand-wine">{formatGBP(campaign.amount_raised || 0)}</p>
              <p className="text-xs text-brand-muted mt-0.5">raised of {formatGBP(campaign.amount_needed)} • {pct}%</p>
            </div>
            <span className="text-brand-primary text-sm font-medium inline-flex items-center gap-1 group-hover:translate-x-1 transition-transform">
              View <ArrowUpRight size={14} strokeWidth={1.8} />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
